<template>
  <div class="expandable-section">
    <div :class="['header-content', 
      { 'expanded': isExpanded }, 
      { 'right-state-icon': stateIconPlace === 'right' },
      { 'active': activeHeaderState }]" 
      @click="handleSectionTitleClick"
      @click.right.prevent="handleSectionTitleClickRight"
      @mousemove="emits('mousemove:header')"
      @mouseleave="emits('mouseleave:header')"
    >
      <!-- A way to override header markup -->
      <template v-if="hasHeaderSlot">
        <slot v-if="hasHeaderSlot" name="header"></slot>
      </template>
      <!-- Default header markup -->
      <template v-else>
        <div class="state-icon-wrapper" @click="handleStateChange">
          <slot v-if="hasStateIconSlot" name="stateIcon" class="state-icon"></slot>
          <span v-else :class="['state-icon', stateIcon]"></span>
        </div>
        <div ref="titleWrapperHtmlElement" class="title-wrapper">
          <span v-if="titleIcon" :class="['title-icon', titleIcon]"></span>
          <slot v-if="hasTitleSlot" name="title"></slot>
          <sl-tooltip v-else hoist :content="title" :disabled="isTitleTooltipDisabled" :placement="titleTooltipPlacement" style="--max-width: 200px;">
            <span :id="titleHtmlElementId" class="title">{{ title }}</span>
          </sl-tooltip>                                                                                                                                             
        </div>
        <div v-if="hasActionButtonSlot" class="action-button-wrapper">
          <slot name="actionButton"></slot>
        </div>
      </template>
    </div>

    <div v-if="showHeaderHelpText" class="header-help-text-wrapper">
      <slot v-if="hasHeaderHelpTextSlot" name="headerHelpText" class="header-help-text"></slot>
      <span v-else class="header-help-text">{{ headerHelpText }}</span>
    </div>

    <!-- Expandable Content -->
    <div v-show="isExpanded" class="expandable-content">
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, toRefs, watch, useSlots, computed, onMounted, onUnmounted } from 'vue';
import { getRandomHtmlElementId, hasHtmlElementOverflow } from '@/helpers/html'; 

interface IMcExpandableSectionProps {
  actionIcon?: string;
  activeHeaderState?: boolean;
  clickableTitle?: boolean;
  expanded?: boolean;
  showTitleTooltip?: boolean;
  stateIcon?: string
  stateIconPlace?: 'left' | 'right';
  title?: string;
  titleIcon?: string;
  titleTooltipPlacement?: string;
  headerHelpText?: string;
}

const props = withDefaults(defineProps<IMcExpandableSectionProps>(), {
  activeHeaderState: false,
  clickableTitle: false,
  expanded: false,
  showTitleTooltip: false,
  stateIcon: "icon-chevron-down",
  stateIconPlace: "left",
  title: "",
  titleTooltipPlacement: "top",
  headerHelpText: "",
});

const {
  activeHeaderState,
  clickableTitle,
  expanded,
  showTitleTooltip,
  stateIcon,
  stateIconPlace,
  title,
  titleIcon,
  titleTooltipPlacement,
  headerHelpText
} = toRefs(props);

let resizeObserver: ResizeObserver | undefined;

const slots = useSlots() as ReturnType<typeof useSlots>;
const hasHeaderSlot = computed(() => !!slots.header);
const hasStateIconSlot = computed(() => !!slots.stateIcon);
const hasTitleSlot = computed(() => !!slots.title);
const hasActionButtonSlot = computed(() => !!slots.actionButton);
const hasHeaderHelpTextSlot = computed(() => !!slots.headerHelpText);

const showHeaderHelpText = computed(() => !!headerHelpText.value || hasHeaderHelpTextSlot.value);

const titleWrapperHtmlElement = ref();

const titleHtmlElementId = ref<string>('');
const isTitleTooltipDisabled = ref(false);

const isExpanded = ref(expanded.value);

const emits = defineEmits([
  "click:right:header",
  "collapse",   
  "expand",
  "mousemove:header", 
  "mouseleave:header"]);

const handleSectionTitleClick = (event: Event) => {
  if (!clickableTitle.value) return;
  handleStateChange(event);
};

const handleSectionTitleClickRight = () => {
  emits("click:right:header");
};

const handleStateChange = (event: Event) => {
  event.stopPropagation();
  event.preventDefault();

  isExpanded.value = !isExpanded.value;
  if (isExpanded.value) {
    emits("expand");
  }
  else {
    emits("collapse");
  }
};

const setControlToInitialState = () => {
  isExpanded.value = false;
};

const setIsTitleTooltipDisabled = () => {
  if (!showTitleTooltip.value || !title.value) {
    isTitleTooltipDisabled.value = true;
  };

  isTitleTooltipDisabled.value = !showTitleTooltip.value || !title.value || !hasHtmlElementOverflow(titleHtmlElementId.value);
};

onMounted(async () => {
  titleHtmlElementId.value = getRandomHtmlElementId('expandable-section-title');
  resizeObserver = new ResizeObserver(setIsTitleTooltipDisabled);
  if (titleWrapperHtmlElement.value) {
    resizeObserver.observe(titleWrapperHtmlElement.value);
  } 
});

onUnmounted(async () => {
  if (resizeObserver && titleWrapperHtmlElement.value) {
    resizeObserver.unobserve(titleWrapperHtmlElement.value);
  }
});

defineExpose({
  setControlToInitialState
});

watch(
  () => expanded.value,
  () => isExpanded.value = expanded.value
);
</script>

<style>
:root {
  --expandable-section-height: unset;
  --expandable-section-header-height: unset;
  --expandable-section-content-height: unset;
  --expandable-section-content-padding: 0;
  --expandable-section-header-padding: 0;
  --expandable-section-header-hover-padding: var(--expandable-section-header-padding);
  --expandable-section-header-background-color-hover: transparent;
  --expandable-section-header-background-color-active: transparent;
  --expandable-section-header-border-radius: 0;
  --expandable-section-state-icon-wrapper-size: 20px;
  --expandable-section-state-icon-wrapper-background-color: transparent;
  --expandable-section-state-icon-wrapper-border-radius: 0;
  --expandable-section-state-icon-wrapper-margin: 0;
  --expandable-section-action-button-wrapper-height: 24px;
  --expandable-section-action-button-wrapper-width: 24px;
  --expandable-section-state-icon-color: var(--colour-icons-neutral-subtle);
  --expandable-section-state-icon-font-size: 24px;
  --expandable-section-state-icon-font-weight: var(--font-weight-body-medium);
  --expandable-section-state-icon-expanded-transform: rotate(-180deg);
  --expandable-section-title-wrapper-width: 100%;
  --expandable-section-title-width: 100%;
  --expandable-section-title-icon-color: var(--colour-icons-contrast);
  --expandable-section-title-icon-font-size: 14px;
  --expandable-section-title-icon-font-weight: var(--font-weight-body-regular);
  --expandable-section-title-icon-padding: 0 10px;
  --expandable-section-title-font-color: var(--colour-text-neutral-subtle);
  --expandable-section-title-font-size: var(--font-size-headers-x-small);
  --expandable-section-title-font-weight: var(--font-weight-body-regular);
  --expandable-section-header-help-text-padding: 0;
  --expandable-section-header-help-text-margin: 0;
  --expandable-section-header-help-text-font-color: var(--colour-text-neutral-subtle);
  --expandable-section-header-help-text-font-size: var(--font-size-body-regular);
  --expandable-section-header-help-text-font-weight: var(--font-weight-body-regular);
  --expandable-section-header-help-text-line-height: var(--line-height-m);
  --expandable-section-header-help-text-style: normal;
}
</style>

<style lang="scss" scoped>
.expandable-section {
  display: flex;
  flex-direction: column;
  height: var(--expandable-section-height);
}

.header-content {
  display: flex;
  align-items: center;
  align-self: stretch;
  overflow: hidden;
  height: var(--expandable-section-header-height);
  padding: var(--expandable-section-header-padding);
  border-radius: var(--expandable-section-header-border-radius);
  cursor: pointer;

  &.expanded {
    .state-icon {
      transform: var(--expandable-section-state-icon-expanded-transform);
    }
  }

  &.right-state-icon {
    flex-direction: row-reverse;
    justify-content: start;
  }

  &.active {
    background-color: var(--expandable-section-header-background-color-active);
  }

  &:hover {
    padding: var(--expandable-section-header-hover-padding);
    background-color: var(--expandable-section-header-background-color-hover);

    .action-button-wrapper {
      display: flex;
    }
  }
}

.state-icon-wrapper {
  display: flex;
  height: var(--expandable-section-state-icon-wrapper-size);
  width: var(--expandable-section-state-icon-wrapper-size);
  justify-content: center;
  align-items: center;
  background-color: var(--expandable-section-state-icon-wrapper-background-color);
  border-radius: var(--expandable-section-state-icon-wrapper-border-radius);
  overflow: hidden;
  flex-shrink: 0;
  margin: var(--expandable-section-state-icon-wrapper-margin);

  .state-icon {
    display: flex;
    justify-content: center;
    font-size: var(--expandable-section-state-icon-font-size);
    font-weight: var(--expandable-section-state-icon-font-weight);
    color: var(--expandable-section-state-icon-color);
    transition: all 0.2s ease-in-out;
  }
}

.title-wrapper {
  display: flex;
  align-items: center;
  width: var(--expandable-section-title-wrapper-width);
  text-wrap: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  user-select: none;

  .title-icon {
    color: var(--expandable-section-title-icon-color);
    font-size: var(--expandable-section-title-icon-font-size);
    font-weight: var(--expandable-section-title-icon-font-weight);
    padding: var(--expandable-section-title-icon-padding);
    flex-shrink: 0;
  }

  .title {
    font-size: var(--expandable-section-title-font-size);
    font-weight: var(--expandable-section-title-font-weight);
    color: var(--expandable-section-title-font-color);
    overflow: hidden;
    text-overflow: ellipsis;
    width: var(--expandable-section-title-width);
    text-wrap: nowrap;
    user-select: none;
  }
}

.action-button-wrapper {
  display: none;
  height: var(--expandable-section-action-button-wrapper-height);
  width: var(--expandable-section-action-button-wrapper-width);
}

.header-help-text-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: var(--expandable-section-header-help-text-padding);
  margin: var(--expandable-section-header-help-text-margin);

  .header-help-text {
    color: var(--expandable-section-header-help-text-font-color);
    font-size: var(--expandable-section-header-help-text-font-size);
    font-weight: var(--expandable-section-header-help-text-font-weight);
    line-height: var(--expandable-section-header-help-text-line-height);
    font-style: var(--expandable-section-header-help-text-style);
  }
}

.expandable-content {
  padding: var(--expandable-section-content-padding);
  height: var(--expandable-section-content-height);
}
</style>
